<template>
    <div class="flex flex-col gap-6">
        <!-- Currently selected -->
        <div
            v-if="currentTenant"
            class="flex flex-col gap-3"
        >
            <span class="text-muval-gray-2 text-[11.9px]">Currently selected</span>
            <div class="border-muval-gray-1 rounded-lg border px-4">
                <TenantCard
                    :tenant="currentTenant"
                    current
                    :show-leave="showLeave"
                    :leaving-tenant-id="leavingTenantId"
                    @leave="$emit('leave', $event)"
                />
            </div>
        </div>

        <!-- Your organisations -->
        <div
            v-if="otherTenants.length > 0"
            class="flex flex-col gap-1"
        >
            <span class="text-muval-gray-2 text-[11.9px]">Your organisations</span>
            <MuvalSearch
                v-if="otherTenants.length > 5"
                v-model="search"
                placeholder="Search organisations"
                onInput
                class="my-1"
            />
            <TenantCard
                v-for="tenant in filteredOtherTenants"
                :key="tenant.id"
                :tenant="tenant"
                :joining="joiningTenantIds.includes(tenant.id)"
                :loading="switchingToTenantId === tenant.id"
                :show-leave="showLeave"
                :leaving-tenant-id="leavingTenantId"
                @select="$emit('select', $event)"
                @leave="$emit('leave', $event)"
            />
        </div>

        <!-- Pending invites -->
        <div
            v-if="pendingInvites.length > 0"
            class="bg-muval-gray-6 rounded-2xl px-6 py-4"
        >
            <span class="text-muval-gray-2 text-[11.9px]">Pending invites</span>
            <div
                v-if="currentInvite"
                class="mt-2"
            >
                <div class="text-muval-gray-1 text-[13.6px]">
                    <span class="font-semibold">{{ currentInvite.tenant?.name }}</span>
                    <span class="font-normal"> invited you</span>
                </div>
                <div class="mt-3 flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <MuvalButton
                            small
                            :loading="acceptingId === currentInvite.tenant?.id"
                            :disabled="decliningId === currentInvite.tenant?.id"
                            @click="$emit('accept', currentInvite)"
                        >
                            Accept
                        </MuvalButton>
                        <MuvalButton
                            small
                            quinary
                            :loading="decliningId === currentInvite.tenant?.id"
                            :disabled="acceptingId === currentInvite.tenant?.id"
                            @click="$emit('decline', currentInvite)"
                        >
                            Decline
                        </MuvalButton>
                    </div>
                    <div
                        v-if="pendingInvites.length > 1"
                        class="flex shrink-0 items-center gap-1.5"
                    >
                        <button
                            class="text-muval-gray-3 hover:text-muval-gray-1 flex size-5 items-center justify-center"
                            @click="prevInvite"
                        >
                            <SvgIcon
                                name="system-chevron-left"
                                class="size-5"
                            />
                        </button>
                        <button
                            class="text-muval-gray-3 hover:text-muval-gray-1 flex size-5 items-center justify-center"
                            @click="nextInvite"
                        >
                            <SvgIcon
                                name="system-chevron-left"
                                class="size-5 rotate-180"
                            />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import TenantCard from '@components/TenantCard.vue';
import { MuvalSearch } from '@components/inputs';

const props = defineProps({
    tenants: { type: Array, required: true },
    pendingInvites: { type: Array, default: () => [] },
    currentTenantId: { type: Number, default: null },
    switchingToTenantId: { type: Number, default: null },
    joiningTenantIds: { type: Array, default: () => [] },
    acceptingId: { type: Number, default: null },
    decliningId: { type: Number, default: null },
    leavingTenantId: { type: Number, default: null },
    showLeave: { type: Boolean, default: true },
});

defineEmits(['select', 'accept', 'decline', 'leave']);

const currentTenant = computed(() => props.tenants.find((t) => t.id === props.currentTenantId) ?? null);

const otherTenants = computed(() => props.tenants.filter((t) => t.id !== props.currentTenantId));

const search = ref('');

const filteredOtherTenants = computed(() => {
    if (!search.value) return otherTenants.value;
    const q = search.value.toLowerCase();
    return otherTenants.value.filter((t) => t.name?.toLowerCase().includes(q));
});

const currentInviteIndex = ref(0);

watch(
    () => props.pendingInvites.length,
    (len) => {
        if (currentInviteIndex.value >= len) {
            currentInviteIndex.value = Math.max(0, len - 1);
        }
    },
);

const currentInvite = computed(() => props.pendingInvites[currentInviteIndex.value] ?? null);

function prevInvite() {
    const len = props.pendingInvites.length;
    currentInviteIndex.value = (currentInviteIndex.value - 1 + len) % len;
}

function nextInvite() {
    const len = props.pendingInvites.length;
    currentInviteIndex.value = (currentInviteIndex.value + 1) % len;
}
</script>
